import { installedApps, text, type WidgetDefinitionSet } from "@norn/widget-renderer/model"

export interface ConsoleAppGroup {
  appId: string
  label: string
  pages: { pageId: string; label: string }[]
}

/** The shell can open context-free pages. Record navigation stays inside the widget,
 * where the renderer owns parameter evaluation, conditions and capability checks. */
export function consoleAppGroups(set: WidgetDefinitionSet, locale: string): ConsoleAppGroup[] {
  return installedApps(set)
    .map((app) => {
      const refs = app.ux.app?.pageRefs.map((ref) => ref.pageId) ?? []
      const pages = app.ux.pages.filter(
        (page) => refs.includes(page.pageId) && !page.contextParams.some((param) => param.required),
      )
      const declaredItems = (app.ux.app?.navigationItemIds ?? []).flatMap((id) => {
        const item = app.ux.navigationItems.find((candidate) => candidate.itemId === id)
        return item ? [item] : []
      })
      const declaredPages = new Set(
        declaredItems.flatMap((item) => (item.destination.kind === "page" ? [item.destination.pageId] : [])),
      )
      const navigation = declaredItems.flatMap((item) => {
        if (item.visibleWhen || item.destination.kind !== "page" || Object.keys(item.destination.parameters).length)
          return []
        const pageId = item.destination.pageId
        const page = pages.find((candidate) => candidate.pageId === pageId)
        return page ? [{ pageId: page.pageId, label: text(item.label, locale) || page.name }] : []
      })
      const ordered = [...navigation]
      for (const page of pages) {
        if (!declaredPages.has(page.pageId) && !ordered.some((item) => item.pageId === page.pageId))
          ordered.push({ pageId: page.pageId, label: page.name })
      }
      return { appId: app.appId, label: app.name, pages: ordered }
    })
    .filter((group) => group.pages.length > 0)
}
